import { TFunction } from 'i18next';
import JamConstants, { EventAudienceType } from '@/src/constants/shared';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { toTitleCase } from '@/src/utils/string.utils';
import { User } from '@/src/types/User';
import { AudienceTypeBasedOnUserGroup } from '@/src/constants/shared/event-audience-type';
import { EventPrivacyType } from '@/src/types/EventPrivacyType';

export const EventAudienceTypeDescriptionDictionary = (t: TFunction) => ({
  [EventAudienceType.SKILL_BUILDER_EVENT]: `${toTitleCase(EventAudienceType.SKILL_BUILDER_EVENT)} - ${t(
    i18nKeys.events.eventDetails.labels.eventAudienceTypeDescriptions.skillBuilderEvent
  )}`,
  [EventAudienceType.AWS_PRIVATE_ENGAGEMENT]: `${toTitleCase(EventAudienceType.AWS_PRIVATE_ENGAGEMENT)} - ${t(
    i18nKeys.events.eventDetails.labels.eventAudienceTypeDescriptions.awsClassroomsBuilderLabs
  )}`,
  [EventAudienceType.AWS_CLASSROOMS_BUILDER_LABS]: `${toTitleCase(EventAudienceType.AWS_CLASSROOMS_BUILDER_LABS)} - ${t(
    i18nKeys.events.eventDetails.labels.eventAudienceTypeDescriptions.awsClassroomsBuilderLabs
  )}`,
  [EventAudienceType.INTERNAL]: `${toTitleCase(EventAudienceType.INTERNAL)} - ${t(
    i18nKeys.events.eventDetails.labels.eventAudienceTypeDescriptions.internal
  )}`,
  [EventAudienceType.PUBLIC]: `${toTitleCase(EventAudienceType.PUBLIC)} - ${t(
    i18nKeys.events.eventDetails.labels.eventAudienceTypeDescriptions.public
  )}`,
  [EventAudienceType.PAID_ALA_CARTE]: `${toTitleCase(EventAudienceType.PAID_ALA_CARTE)} - ${t(
    i18nKeys.events.eventDetails.labels.eventAudienceTypeDescriptions.paidAlaCarte
  )}`,
  [EventAudienceType.GLOBAL_PARTNERS]: `${toTitleCase(EventAudienceType.GLOBAL_PARTNERS)} - ${t(
    i18nKeys.events.eventDetails.labels.eventAudienceTypeDescriptions.globalPartners
  )}`,
  [EventAudienceType.AWS_EDUCATION]: `${toTitleCase(EventAudienceType.AWS_EDUCATION)} - ${t(
    i18nKeys.events.eventDetails.labels.eventAudienceTypeDescriptions.awsEducation
  )}`,
  [EventAudienceType.RECRUITING]: `${toTitleCase(EventAudienceType.RECRUITING)} - ${t(
    i18nKeys.events.eventDetails.labels.eventAudienceTypeDescriptions.recruiting
  )}`,
  [EventAudienceType.ACI_EVENT]: `${toTitleCase(EventAudienceType.ACI_EVENT)} - ${t(
    i18nKeys.events.eventDetails.labels.eventAudienceTypeDescriptions.aciEvent
  )}`,
  [EventAudienceType.SKILL_BUILDER_LEARNER_EVENT]: `${toTitleCase(EventAudienceType.SKILL_BUILDER_LEARNER_EVENT)} - ${t(
    i18nKeys.events.eventDetails.labels.eventAudienceTypeDescriptions.sbtsLearnerEvent
  )}`,
  [EventAudienceType.SUMMIT]: `${toTitleCase(EventAudienceType.SUMMIT)} - ${t(
    i18nKeys.events.eventDetails.labels.eventAudienceTypeDescriptions.summit,
    {
      reInvent: JamConstants.RE_INVENT,
      reInforce: JamConstants.RE_INFORCE,
    }
  )}`,
  [EventAudienceType.SKILL_BUILDER_COHORT]: `${toTitleCase(EventAudienceType.SKILL_BUILDER_COHORT)} - ${t(
    i18nKeys.events.eventDetails.labels.eventAudienceTypeDescriptions.skillBuilderCohort
  )}`,
});

export const channelsForUserGroup: (user: User | null) => Record<string, boolean> = (user) => {
  if (!user) return {};
  if (user.isSuperAdmin) {
    return AudienceTypeBasedOnUserGroup.JAM_ADMIN_USER;
  }
  if (user.isAmazonian) {
    return AudienceTypeBasedOnUserGroup.FEDERATE_USER;
  }
  if (user.isSubscribedUser) {
    return AudienceTypeBasedOnUserGroup.SB_ADMIN;
  }
  if (user.isSbtsLearner) {
    return AudienceTypeBasedOnUserGroup.SB_LEARNER;
  }
  return {};
};

export const getCurrentAudienceForEvent = (audienceValue: string, t: TFunction) => {
  const audienceDictionary = EventAudienceTypeDescriptionDictionary(t);
  return {
    label: audienceDictionary[audienceValue],
    value: audienceValue,
  };
};

export const buildAudienceSelectOptionsForUser = (user: User | null, t: TFunction) => {
  const audienceDictionary = EventAudienceTypeDescriptionDictionary(t);
  return Object.entries(channelsForUserGroup(user)).map(([item]) => ({
    label: audienceDictionary[item],
    value: item,
  }));
};

export const getDefaultAudienceForGroup = (user: User | null) => {
  if (!user) return '';
  const channels = channelsForUserGroup(user);
  return Object.keys(channels).find((key) => channels[key]);
};

export const getAvailablePrivacyTypesForUser = (user: User | null, audienceType: string | null, t: TFunction) => {
  const AdminEventPrivacyTypes = Object.keys(EventPrivacyType).map((type) => ({
    label: t(i18nKeys.events.eventDetails.labels.evenPrivacyType[type as keyof typeof EventPrivacyType]),
    value: EventPrivacyType[type as keyof typeof EventPrivacyType],
    description: t(i18nKeys.events.eventDetails.labels.evenPrivacyTypeDesc[type as keyof typeof EventPrivacyType]),
  }));

  const EventPrivacyTypes = AdminEventPrivacyTypes.filter((f) => {
    if (audienceType === EventAudienceType.SUMMIT) {
      return f.value !== EventPrivacyType.PUBLIC && f.value !== EventPrivacyType.PRIVATE_CODE;
    }
    return f.value !== EventPrivacyType.PUBLIC;
  });
  const SbtsLearnerPrivacyTypes = AdminEventPrivacyTypes.filter((f) => f.value === EventPrivacyType.PRIVATE_INVITE);

  return user?.isSuperAdmin
    ? AdminEventPrivacyTypes
    : user?.isSubscribedUser || !user?.isSbtsLearner
    ? EventPrivacyTypes
    : SbtsLearnerPrivacyTypes;
};
